<script setup lang="ts">
import type { BreadCrumbsProps } from './bread-crumbs';
import HugeIcon from '../HugeIcon/HugeIcon.vue';
import IconButton from '../IconButton/IconButton.vue';
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';

const props = defineProps<BreadCrumbsProps>();
const emits = defineEmits<{
  back: [];
  edit: [];
  navigate: [href: string];
}>();

const containerRef = ref<HTMLElement | null>(null);
const measureFullRef = ref<HTMLElement | null>(null);
const isCollapsed = ref(false);

const collapseEnabled = computed<boolean>(
  () => props.layout !== 'stacked' && props.responsiveCollapse === 'last-item',
);
const isCollapsedView = computed<boolean>(
  () => collapseEnabled.value && isCollapsed.value,
);
const lastBreadCrumb = computed(() => {
  return props.breadCrumbs[props.breadCrumbs.length - 1];
});
const parentBreadCrumbs = computed(() => props.breadCrumbs.slice(0, -1));

let resizeObserver: ResizeObserver | null = null;

function updateCollapseState() {
  if (!collapseEnabled.value) {
    isCollapsed.value = false;
    return;
  }

  const container = containerRef.value;
  const measurementRow = measureFullRef.value;

  if (!container || !measurementRow) {
    isCollapsed.value = false;
    return;
  }

  isCollapsed.value = measurementRow.scrollWidth > container.clientWidth;
}

function scheduleCollapseUpdate() {
  void nextTick(updateCollapseState);
}

watch(
  () => [
    props.responsiveCollapse,
    props.layout,
    props.showBackButton,
    props.showEditButton,
    ...props.breadCrumbs.map(
      (breadcrumb) => `${breadcrumb.label}|${breadcrumb.href ?? ''}`,
    ),
  ],
  () => {
    scheduleCollapseUpdate();
  },
);

onMounted(() => {
  scheduleCollapseUpdate();

  if (typeof ResizeObserver !== 'undefined' && containerRef.value) {
    resizeObserver = new ResizeObserver(() => {
      updateCollapseState();
    });
    resizeObserver.observe(containerRef.value);
    return;
  }

  window.addEventListener('resize', updateCollapseState);
});

onBeforeUnmount(() => {
  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
    return;
  }

  window.removeEventListener('resize', updateCollapseState);
});
</script>

<template>
  <div ref="containerRef" class="relative w-full min-w-0">
    <div v-if="layout === 'stacked'" class="flex min-w-0 items-center gap-4">
      <IconButton
        v-if="showBackButton"
        icon="back"
        variant="neutral"
        class="shrink-0"
        @click="emits('back')"
      />
      <div class="flex min-w-0 flex-1 flex-col gap-1">
        <nav
          v-if="parentBreadCrumbs.length"
          class="flex min-w-0 items-center gap-2 text-xs leading-5 text-slate-500"
        >
          <template
            v-for="(breadcrumb, index) in parentBreadCrumbs"
            :key="index"
          >
            <span
              v-if="index"
              aria-hidden="true"
              class="shrink-0 text-slate-300"
              >/</span
            >
            <a
              v-if="breadcrumb.href"
              :href="breadcrumb.href"
              :title="breadcrumb.label"
              class="hover:text-primary-500 min-w-0 truncate"
              @click.prevent="emits('navigate', breadcrumb.href)"
              >{{ breadcrumb.label }}</a
            >
            <span v-else :title="breadcrumb.label" class="min-w-0 truncate">{{
              breadcrumb.label
            }}</span>
          </template>
        </nav>
        <div
          v-if="lastBreadCrumb"
          class="flex min-w-0 items-center gap-2 text-base leading-6 font-semibold text-slate-900"
        >
          <slot name="current-prefix" />
          <a
            v-if="lastBreadCrumb.href"
            :href="lastBreadCrumb.href"
            :title="lastBreadCrumb.label"
            class="hover:text-primary-500 min-w-0 truncate"
            aria-current="page"
            @click.prevent="emits('navigate', lastBreadCrumb.href)"
            >{{ lastBreadCrumb.label }}</a
          >
          <span
            v-else
            :title="lastBreadCrumb.label"
            class="min-w-0 truncate"
            aria-current="page"
            >{{ lastBreadCrumb.label }}</span
          >
          <button
            v-if="showEditButton"
            type="button"
            class="-ml-1 shrink-0 rounded-md p-1 text-slate-400 hover:bg-slate-100"
            @click="emits('edit')"
          >
            <HugeIcon icon="edit" :size="18" />
          </button>
          <slot name="current-suffix" />
        </div>
      </div>
    </div>
    <nav
      v-else
      class="flex min-w-0 items-center gap-2 text-[11px] font-bold tracking-[0.15em] text-slate-400 uppercase"
    >
      <IconButton
        v-if="showBackButton"
        icon="back"
        variant="neutral"
        class="mr-2 shrink-0"
        @click="emits('back')"
      />
      <template v-if="isCollapsedView && lastBreadCrumb">
        <div class="flex min-w-0 flex-1 items-center justify-start gap-2">
          <a
            v-if="lastBreadCrumb.href"
            :href="lastBreadCrumb.href"
            class="hover:text-primary-500 max-w-full min-w-0 shrink truncate whitespace-nowrap text-slate-600 transition-colors"
            @click.prevent="emits('navigate', lastBreadCrumb.href)"
          >
            {{ lastBreadCrumb.label }}
          </a>
          <span
            v-else
            class="max-w-full min-w-0 shrink truncate whitespace-nowrap text-slate-600"
          >
            {{ lastBreadCrumb.label }}
          </span>
          <button
            v-if="showEditButton"
            class="-ml-1 shrink-0 rounded-md p-1 hover:bg-slate-100"
            @click="emits('edit')"
          >
            <HugeIcon icon="edit" :size="18"></HugeIcon>
          </button>
        </div>
      </template>
      <template v-else>
        <template
          v-for="(breadcrumb, index) in props.breadCrumbs"
          :key="breadcrumb.label"
        >
          <a
            v-if="breadcrumb.href"
            :href="breadcrumb.href"
            class="hover:text-primary-500 whitespace-nowrap transition-colors"
            @click.prevent="emits('navigate', breadcrumb.href)"
          >
            {{ breadcrumb.label }}
          </a>
          <span
            v-else-if="index < props.breadCrumbs.length - 1"
            class="whitespace-nowrap"
          >
            {{ breadcrumb.label }}
          </span>
          <span v-else class="whitespace-nowrap text-slate-600">
            {{ breadcrumb.label }}
          </span>
          <span
            v-if="index < props.breadCrumbs.length - 1"
            class="text-slate-300"
            >/</span
          >
        </template>
      </template>
      <button
        v-if="showEditButton && !(isCollapsedView && lastBreadCrumb)"
        class="-ml-1 shrink-0 rounded-md p-1 hover:bg-slate-100"
        @click="emits('edit')"
      >
        <HugeIcon icon="edit" :size="18"></HugeIcon>
      </button>
    </nav>

    <nav
      v-if="collapseEnabled"
      ref="measureFullRef"
      aria-hidden="true"
      class="pointer-events-none absolute top-0 left-0 z-[-1] inline-flex w-max items-center gap-2 text-[11px] font-bold tracking-[0.15em] text-slate-400 uppercase opacity-0"
    >
      <IconButton
        v-if="showBackButton"
        icon="back"
        variant="neutral"
        class="mr-2 shrink-0"
      />
      <template
        v-for="(breadcrumb, index) in props.breadCrumbs"
        :key="`${breadcrumb.label}-measurement`"
      >
        <a
          v-if="breadcrumb.href"
          :href="breadcrumb.href"
          class="hover:text-primary-500 whitespace-nowrap transition-colors"
          @click.prevent="emits('navigate', breadcrumb.href)"
        >
          {{ breadcrumb.label }}
        </a>
        <span
          v-else-if="index < props.breadCrumbs.length - 1"
          class="whitespace-nowrap"
        >
          {{ breadcrumb.label }}
        </span>
        <span v-else class="whitespace-nowrap text-slate-600">
          {{ breadcrumb.label }}
        </span>
        <span v-if="index < props.breadCrumbs.length - 1" class="text-slate-300"
          >/</span
        >
      </template>
      <button
        v-if="showEditButton"
        class="-ml-1 shrink-0 rounded-md p-1 hover:bg-slate-100"
        tabindex="-1"
      >
        <HugeIcon icon="edit" :size="18"></HugeIcon>
      </button>
    </nav>
  </div>
</template>
